import React, { useState } from 'react';
import { DataTable } from '../components/DataTable';
import { WizardForm } from '../components/WizardForm';
import { StatusBadge } from '../components/StatusBadge';
import { ShieldAlertIcon, CalculatorIcon, CheckCircleIcon } from 'lucide-react';
export function BLCManagement() {
  const [activeTab, setActiveTab] = useState<'list' | 'issue' | 'renew'>('list');
  const [currentStep, setCurrentStep] = useState(0);
  // Form State
  const [formData, setFormData] = useState({
    ownerNid: '',
    ownerName: '',
    vesselType: '',
    length: '',
    breadth: '',
    depth: '',
    weightCapacity: '',
    tonnage: 0,
    annualFee: 0,
    porCheckStatus: 'pending',
    blcNumber: ''
  });
  // Mock Data
  const blcList = [{
    blcNumber: 'BLC-2024-001',
    ownerName: 'Rahim Uddin',
    vesselType: 'Boat',
    tonnage: '5.2',
    issueDate: '2024-01-15',
    expiryDate: '2025-01-14',
    status: 'active' as const
  }, {
    blcNumber: 'BLC-2023-089',
    ownerName: 'Karim Sheikh',
    vesselType: 'Trawler',
    tonnage: '12.5',
    issueDate: '2023-05-20',
    expiryDate: '2024-05-19',
    status: 'expired' as const
  }];
  const columns = [{
    key: 'blcNumber',
    label: 'BLC Number'
  }, {
    key: 'ownerName',
    label: 'Owner Name'
  }, {
    key: 'vesselType',
    label: 'Vessel Type'
  }, {
    key: 'tonnage',
    label: 'Tonnage'
  }, {
    key: 'issueDate',
    label: 'Issue Date'
  }, {
    key: 'expiryDate',
    label: 'Expiry Date'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-381" />
  }];
  const wizardSteps = [{
    title: 'Owner Details',
    description: 'Enter owner NID and verify'
  }, {
    title: 'Boat Measurement',
    description: 'Enter dimensions (L, B, D)'
  }, {
    title: 'POR Check & Fee',
    calculate: true,
    description: 'System checks and calculates fee'
  }, {
    title: 'Issue BLC',
    description: 'Generate digital BLC'
  }];
  const calculateTonnageAndFee = () => {
    const l = parseFloat(formData.length) || 0;
    const b = parseFloat(formData.breadth) || 0;
    const d = parseFloat(formData.depth) || 0;
    // Simple mock formula for tonnage: (L * B * D) / 100
    const calculatedTonnage = l * b * d / 100;
    // Mock fee: 500 base + 100 per ton
    const calculatedFee = 500 + calculatedTonnage * 100;
    setFormData(prev => ({
      ...prev,
      tonnage: Number(calculatedTonnage.toFixed(2)),
      annualFee: Number(calculatedFee.toFixed(2)),
      // Simulate POR check - 10% chance of being flagged
      porCheckStatus: Math.random() > 0.9 ? 'flagged' : 'clear'
    }));
  };
  return <div className="space-y-6" data-id="element-382">
      <div data-id="element-383">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-384">BLC Management</h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-385">
          Boat License Certificate issuance, renewal, and measurement logging
        </p>
      </div>

      <div className="border-b border-gray-200" data-id="element-386">
        <div className="flex gap-4" data-id="element-387">
          <button onClick={() => setActiveTab('list')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'list' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-388">
            BLC Registry
          </button>
          <button onClick={() => setActiveTab('issue')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'issue' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-389">
            Issue New BLC
          </button>
          <button onClick={() => setActiveTab('renew')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'renew' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-390">
            Renew BLC
          </button>
        </div>
      </div>

      {activeTab === 'list' && <DataTable columns={columns} data={blcList} searchPlaceholder="Search BLC or Owner..." data-id="element-391" />}

      {activeTab === 'issue' && <WizardForm steps={wizardSteps} currentStep={currentStep} onStepChange={step => {
      if (step === 2 && currentStep === 1) {
        calculateTonnageAndFee();
      }
      setCurrentStep(step);
    }} onSubmit={() => {
      alert('Digital BLC Issued Successfully!');
      setCurrentStep(0);
      setActiveTab('list');
    }} data-id="element-392">
          {currentStep === 0 && <div className="space-y-4" data-id="element-393">
              <div data-id="element-394">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-395">
                  Owner NID *
                </label>
                <div className="flex gap-2" data-id="element-396">
                  <input type="text" value={formData.ownerNid} onChange={e => setFormData({
              ...formData,
              ownerNid: e.target.value
            })} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5E20]" placeholder="Enter 10 or 13 digit NID" data-id="element-397" />
                  <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium" data-id="element-398">
                    Verify
                  </button>
                </div>
              </div>
              <div data-id="element-399">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-400">
                  Owner Name
                </label>
                <input type="text" value={formData.ownerName} onChange={e => setFormData({
            ...formData,
            ownerName: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5E20]" data-id="element-401" />
              </div>
            </div>}

          {currentStep === 1 && <div className="space-y-4" data-id="element-402">
              <div data-id="element-403">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-404">
                  Vessel Type *
                </label>
                <select value={formData.vesselType} onChange={e => setFormData({
            ...formData,
            vesselType: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5E20]" data-id="element-405">
                  <option value="" data-id="element-406">Select Type</option>
                  <option value="Boat" data-id="element-407">Boat</option>
                  <option value="Trawler" data-id="element-408">Trawler</option>
                  <option value="Launch" data-id="element-409">Launch</option>
                </select>
              </div>
              <div className="grid grid-cols-3 gap-4" data-id="element-410">
                <div data-id="element-411">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-412">
                    Length (ft)
                  </label>
                  <input type="number" value={formData.length} onChange={e => setFormData({
              ...formData,
              length: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5E20]" data-id="element-413" />
                </div>
                <div data-id="element-414">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-415">
                    Breadth (ft)
                  </label>
                  <input type="number" value={formData.breadth} onChange={e => setFormData({
              ...formData,
              breadth: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5E20]" data-id="element-416" />
                </div>
                <div data-id="element-417">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-418">
                    Depth (ft)
                  </label>
                  <input type="number" value={formData.depth} onChange={e => setFormData({
              ...formData,
              depth: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5E20]" data-id="element-419" />
                </div>
              </div>
              <div data-id="element-420">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-421">
                  Weight Capacity (Tons)
                </label>
                <input type="number" value={formData.weightCapacity} onChange={e => setFormData({
            ...formData,
            weightCapacity: e.target.value
          })} placeholder="Enter weight capacity" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5E20]" data-id="element-422" />
              </div>
            </div>}

          {currentStep === 2 && <div className="space-y-6" data-id="element-423">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-424">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-425">
                  <CalculatorIcon size={20} className="text-[#1B5E20]" data-id="element-426" />
                  Calculated Metrics
                </h4>
                <div className="grid grid-cols-2 gap-4" data-id="element-427">
                  <div className="bg-white p-3 rounded border border-gray-200" data-id="element-428">
                    <div className="text-sm text-gray-500" data-id="element-429">
                      Calculated Tonnage
                    </div>
                    <div className="text-xl font-bold text-gray-900" data-id="element-430">
                      {formData.tonnage} Tons
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded border border-gray-200" data-id="element-431">
                    <div className="text-sm text-gray-500" data-id="element-432">Annual Fee</div>
                    <div className="text-xl font-bold text-[#1B5E20]" data-id="element-433">
                      ৳ {formData.annualFee}
                    </div>
                  </div>
                </div>
              </div>

              <div className={`p-4 rounded-lg border ${formData.porCheckStatus === 'clear' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-id="element-434">
                <h4 className={`font-semibold mb-2 flex items-center gap-2 ${formData.porCheckStatus === 'clear' ? 'text-green-800' : 'text-red-800'}`} data-id="element-435">
                  {formData.porCheckStatus === 'clear' ? <>
                      <CheckCircleIcon size={20} data-id="element-436" /> Offender Check Passed
                    </> : <>
                      <ShieldAlertIcon size={20} data-id="element-437" /> Active POR Found
                    </>}
                </h4>
                <p className={`text-sm ${formData.porCheckStatus === 'clear' ? 'text-green-700' : 'text-red-700'}`} data-id="element-438">
                  {formData.porCheckStatus === 'clear' ? 'No active Forest Offense Reports found for this owner. Safe to proceed.' : 'Owner is flagged in the Offender Module. BLC issuance is blocked until cleared.'}
                </p>
              </div>

              {formData.porCheckStatus === 'flagged' && <div className="text-sm text-red-600 font-medium" data-id="element-439">
                  Cannot proceed to next step due to active POR.
                </div>}
            </div>}

          {currentStep === 3 && <div className="space-y-6" data-id="element-440">
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center" data-id="element-441">
                <CheckCircleIcon size={48} className="mx-auto text-green-600 mb-4" data-id="element-442" />
                <h3 className="text-lg font-bold text-green-900 mb-2" data-id="element-443">
                  Ready to Issue Digital BLC
                </h3>
                <p className="text-sm text-green-700 mb-4" data-id="element-444">
                  All checks passed. The BLC will be linked to the owner's Smart
                  ID.
                </p>
                <div className="inline-block bg-white px-4 py-2 rounded-lg border border-green-200 font-mono font-bold text-lg text-gray-900" data-id="element-445">
                  BLC-2024-
                  {Math.floor(Math.random() * 1000).toString().padStart(3, '0')}
                </div>
              </div>
            </div>}
        </WizardForm>}

      {activeTab === 'renew' && <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-446">
          <h3 className="text-lg font-semibold mb-4" data-id="element-447">BLC Renewal Workflow</h3>
          <div className="flex gap-4 mb-6" data-id="element-448">
            <input type="text" placeholder="Enter existing BLC Number" className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5E20]" data-id="element-449" />
            <button className="px-6 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32]" data-id="element-450">
              Search
            </button>
          </div>
          <div className="text-center text-gray-500 py-8" data-id="element-451">
            Search for an existing BLC to initiate the renewal process.
          </div>
        </div>}
    </div>;
}